import { View, Text, styled, ScrollView } from 'tamagui'
import { X } from '@tamagui/lucide-icons'
import { useAppStore } from '@/store'
import { messages, threadMessages, users } from '@/mocks'
import { Avatar } from '../../common'

const Panel = styled(View, {
  width: 340,
  backgroundColor: '$sidebar',
  borderLeftWidth: 1,
  borderLeftColor: '$borderDefault',
  flexDirection: 'column',
  flexShrink: 0,
})

const PanelHeader = styled(View, {
  height: 48,
  paddingHorizontal: 16,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
})

const HeaderTitle = styled(Text, {
  fontFamily: '$fontHeading',
  fontWeight: '600',
  fontSize: 15,
  color: '$surfaceForeground',
})

const CloseBtn = styled(View, {
  width: 28,
  height: 28,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: { backgroundColor: '$sidebarHover' },
})

const ThreadItem = styled(View, {
  flexDirection: 'row',
  gap: 10,
  padding: 12,
  cursor: 'pointer',
  borderRadius: '$borderRadiusMd',
  hoverStyle: { backgroundColor: '$sidebarHover' },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 100ms ease' },
})

const ThreadContent = styled(View, {
  flex: 1,
  gap: 2,
  minWidth: 0,
})

const ThreadAuthor = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 13,
  fontWeight: '600',
  color: '$surfaceForeground',
})

const ThreadPreview = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 12,
  color: '$sidebarMuted',
  lineHeight: 18,
  // @ts-expect-error -- web overflow
  style: { overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' },
})

const ReplyCount = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 12,
  color: '$accent',
  fontWeight: '600',
})

const EmptyState = styled(View, {
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
  padding: 32,
})

const EmptyText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 13,
  color: '$sidebarMuted',
  textAlign: 'center',
})

interface ThreadListPanelProps {
  channelId: string
}

export function ThreadListPanel({ channelId }: ThreadListPanelProps) {
  const { toggleThreadList, openThread } = useAppStore()

  const channelMessages = messages[channelId] || []
  const threaded = channelMessages.filter((m) => m.threadCount && m.threadCount > 0)

  return (
    <Panel>
      <PanelHeader>
        <HeaderTitle>Threads</HeaderTitle>
        <CloseBtn onPress={toggleThreadList}>
          <X size={16} color="$sidebarMuted" />
        </CloseBtn>
      </PanelHeader>

      {threaded.length === 0 ? (
        <EmptyState>
          <EmptyText>No threads in this channel</EmptyText>
        </EmptyState>
      ) : (
        <ScrollView flex={1} showsVerticalScrollIndicator={false}>
          <View padding={8} gap={2}>
            {threaded.map((msg) => {
              const author = users[msg.authorId]
              if (!author) return null
              const replies = threadMessages[msg.id] || []
              return (
                <ThreadItem key={msg.id} onPress={() => openThread(msg.id)}>
                  <Avatar
                    name={author.displayName}
                    color={author.roleColor || '#2A3142'}
                    size="sm"
                    showStatus={false}
                  />
                  <ThreadContent>
                    <ThreadAuthor color={author.roleColor || '$surfaceForeground'}>
                      {author.displayName}
                    </ThreadAuthor>
                    <ThreadPreview>{msg.content}</ThreadPreview>
                    <ReplyCount>
                      {replies.length || msg.threadCount} {(replies.length || msg.threadCount || 0) === 1 ? 'reply' : 'replies'}
                    </ReplyCount>
                  </ThreadContent>
                </ThreadItem>
              )
            })}
          </View>
        </ScrollView>
      )}
    </Panel>
  )
}
